import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import styles from './AnimatedTabs.module.css';

export interface AnimatedTabItem<T extends string = string> {
  id: T;
  label: ReactNode;
  disabled?: boolean;
}

interface AnimatedTabsProps<T extends string> {
  items: readonly AnimatedTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  onSelectionFeedback?: () => void;
  ariaLabel: string;
  className?: string;
}

const PILL_SPRING = { type: 'spring', stiffness: 420, damping: 26, mass: 0.72 } as const;

/**
 * A horizontally scrollable segmented control shared by storefront and admin.
 * It keeps one option visible beyond the selection, matching Telegram's tab
 * rail, and uses roving focus so arrow keys work without visiting every tab.
 */
export function AnimatedTabs<T extends string>({
  items,
  value,
  onChange,
  onSelectionFeedback,
  ariaLabel,
  className,
}: AnimatedTabsProps<T>) {
  const reduceMotion = useReducedMotion();
  const instanceId = useId();
  const railRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const requestedIndex = items.findIndex((item) => item.id === value);
  const activeIndex = Math.max(0, requestedIndex);
  const activeId = items[activeIndex]?.id ?? '';
  const focusableId = items[activeIndex]?.disabled ? items.find((item) => !item.disabled)?.id : activeId;
  const previousIndex = useRef(activeIndex);
  const direction = Math.sign(activeIndex - previousIndex.current);
  const aheadIndex = Math.max(
    0,
    Math.min(items.length - 1, activeIndex + (direction || 1)),
  );
  const aheadId = items[aheadIndex]?.id ?? '';

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const rail = railRef.current;
      const ahead = itemRefs.current.get(aheadId);
      if (!rail || !ahead) return;

      // Rects share the same viewport coordinate system, unlike offsetLeft,
      // whose ancestor changes when a transformed route is mounted.
      const railRect = rail.getBoundingClientRect();
      const aheadRect = ahead.getBoundingClientRect();
      const gutter = 10;
      let delta = 0;
      if (aheadRect.right > railRect.right - gutter) {
        delta = aheadRect.right - railRect.right + gutter;
      } else if (aheadRect.left < railRect.left + gutter) {
        delta = aheadRect.left - railRect.left - gutter;
      }

      if (delta !== 0) {
        rail.scrollBy({
          left: delta,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    });

    previousIndex.current = activeIndex;
    return () => window.cancelAnimationFrame(frame);
  }, [activeIndex, aheadId, reduceMotion]);

  const select = (id: T, focus = false) => {
    const item = items.find((candidate) => candidate.id === id);
    if (!item || item.disabled) return;
    if (id !== activeId) {
      onSelectionFeedback?.();
      onChange(id);
    }
    if (focus) window.requestAnimationFrame(() => itemRefs.current.get(id)?.focus());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, focusedId: T) => {
    const available = items.map((item, index) => item.disabled ? -1 : index).filter((index) => index >= 0);
    const focusedIndex = items.findIndex((item) => item.id === focusedId);
    let nextIndex: number | undefined;
    if (event.key === 'ArrowRight') nextIndex = available.find((index) => index > focusedIndex);
    else if (event.key === 'ArrowLeft') nextIndex = [...available].reverse().find((index) => index < focusedIndex);
    else if (event.key === 'Home') nextIndex = available[0];
    else if (event.key === 'End') nextIndex = available[available.length - 1];
    else return;
    event.preventDefault();
    if (nextIndex === undefined) return;
    const next = items[nextIndex];
    if (next) select(next.id, true);
  };

  return (
    <LayoutGroup id={instanceId}>
      <div
        ref={railRef}
        className={[styles.tabs, className].filter(Boolean).join(' ')}
        role="tablist"
        aria-label={ariaLabel}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <button
              key={item.id}
              ref={(node) => {
                if (node) itemRefs.current.set(item.id, node);
                else itemRefs.current.delete(item.id);
              }}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={item.disabled}
              tabIndex={!item.disabled && item.id === focusableId ? 0 : -1}
              className={active ? styles.tabActive : styles.tab}
              onClick={() => select(item.id)}
              onKeyDown={(event) => handleKeyDown(event, item.id)}
            >
              {active ? (
                <motion.span
                  layoutId="pill"
                  className={styles.pill}
                  transition={reduceMotion ? { duration: 0 } : PILL_SPRING}
                />
              ) : null}
              <span className={styles.content}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
}
